import React from "react";
import Link, { LinkProps } from "next/link";
import type { UrlObject } from "url";
import {
  AppWindowMac,
  ExternalLink as ExternalLinkIcon,
  ListChecks,
  Server
} from "lucide-react";
import { cn, retainAttribution } from "@/lib/utils";

type Props = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & LinkProps;

/** Renders NextJS link or directly children if no href provided */
export const OptionalLink = ({
  children,
  href,
  ...props
}: Omit<Props, "href"> & {
  href?: string | UrlObject;
  children?: React.ReactNode;
}) =>
  href ? (
    <Link href={href} {...props}>
      {children}
    </Link>
  ) : (
    <>{children}</>
  );

export const MarketingLink = ({
  children,
  href,
  className,
  ...props
}: Omit<Props, "href" | "scroll"> & {
  href: string;
}) => (
  <Link href={retainAttribution(href)} className={cn("MarketingLink", className)} {...props}>
    {children}
  </Link>
);

export const ExternalLink = ({
  children,
  href,
  className,
  rel,
  target,
  ...props
}: Omit<Props, "href"> & {
  href?: string | UrlObject;
  children?: React.ReactNode;
}) =>
  href || props.onClick ? (
    <Link
      href={href ?? ""}
      className={cn("group inline-flex items-center", className)}
      target={target ?? "_blank"}
      rel={rel ?? "noopener noreferrer"}
      {...props}
    >
      {children}
      {href && <ExternalLinkIcon className={cn("ml-1 w-4 h-4 shrink-0 hidden sm:block")} />}
    </Link>
  ) : (
    <>{children}</>
  );

export const ObjectLink = ({
  children,
  href,
  className,
  type,
  ...props
}: Omit<Props, "href"> & {
  href?: string | UrlObject;
  children?: React.ReactNode;
  type?: "host" | "app" | "task";
}) =>
  href ? (
    <Link href={href} className={cn("group inline-flex items-center", className)} {...props}>
      {type === "task" && (
        <ListChecks className="mr-1 w-4 h-4 text-foreground/60 group-hover:text-foreground" />
      )}
      {type === "host" && (
        <Server className="mr-1 w-4 h-4 text-foreground/60 group-hover:text-foreground" />
      )}
      {type === "app" && (
        <AppWindowMac className="mr-1 w-4 h-4 text-foreground/60 group-hover:text-foreground" />
      )}
      {children}
    </Link>
  ) : (
    <>{children}</>
  );
